import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { db } from '../services/backend';
import { useToast } from '../contexts/ToastContext';
import { logger } from '../utils/logger';

const AFFILIATE_STORAGE_KEY = 'Veraz_affiliate';
const AFFILIATE_EXPIRY_DAYS = 60;

interface StoredAffiliateData {
  affiliateId: string;
  affiliateCode: string;
  name: string;
  timestamp: number;
}

const AffiliateTracker: React.FC = () => {
  const location = useLocation();
  const { addToast } = useToast();

  useEffect(() => {
    const checkAffiliate = async () => {
      const params = new URLSearchParams(location.search);
      const utmSource = params.get('utm_source');
      const utmAffid = params.get('utm_affid');
      const ref = params.get('ref');

      const affiliateCode = utmSource === 'affiliate' && utmAffid ? utmAffid : ref;

      if (affiliateCode) {
        // Look up affiliate by code
        try {
          const affiliate = await db.getAffiliateByCode(affiliateCode);
          if (affiliate) {
            const data: StoredAffiliateData = {
              affiliateId: affiliate.id,
              affiliateCode: affiliate.code,
              name: affiliate.name,
              timestamp: Date.now(),
            };
            localStorage.setItem(AFFILIATE_STORAGE_KEY, JSON.stringify(data));

            // Dispatch event for reactive updates
            window.dispatchEvent(new Event('affiliate-update'));

            // Increment clicks
            await db.incrementAffiliateClicks(affiliate.id);

            logger.debug(`Affiliate tracked: ${affiliate.name} (${affiliate.code})`);
            addToast(`Você está apoiando o parceiro: ${affiliate.name}`, 'success');
          }
        } catch (error) {
          console.error('Error tracking affiliate:', error);
        }
      }
    };

    checkAffiliate();
  }, [location]);

  // Cleanup expired data on mount
  useEffect(() => {
    const stored = localStorage.getItem(AFFILIATE_STORAGE_KEY);
    if (stored) {
      try {
        const data: StoredAffiliateData = JSON.parse(stored);
        const now = Date.now();
        const daysDiff = (now - data.timestamp) / (1000 * 60 * 60 * 24);

        if (daysDiff > AFFILIATE_EXPIRY_DAYS) {
          localStorage.removeItem(AFFILIATE_STORAGE_KEY);
          logger.debug('Affiliate data expired and removed.');
        }
      } catch (e) {
        localStorage.removeItem(AFFILIATE_STORAGE_KEY);
      }
    }
  }, []);

  return null;
};

export default AffiliateTracker;

export const getStoredAffiliate = (): { affiliateId: string; affiliateCode: string } | null => {
  const stored = localStorage.getItem(AFFILIATE_STORAGE_KEY);
  if (!stored) return null;
  try {
    const data: StoredAffiliateData = JSON.parse(stored);
    return { affiliateId: data.affiliateId, affiliateCode: data.affiliateCode };
  } catch {
    return null;
  }
};
